import { Box, Stack, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { ApprovalEvent } from "models/LLMs/LLMClassifierApproval";
import { APPROVAL_EVENT_ICONS } from "../../utils/llmClassifierApproval";

interface NoticeProps {
    isOpen: boolean;
    onClose: () => void;
    event: ApprovalEvent;
    /** Who took the action the email announced. */
    actorName: string;
    /** The action's local date, already formatted. */
    eventDate?: string;
    comment?: string;
    versionLabel: string;
    /** The published version, when one other than this version is live. */
    liveVersionLabel?: string;
    isThisVersionLive: boolean;
    action?: { label: string; onClick: () => void };
}

/** The title and the paragraph that explains what the email's outcome means for this version. */
function noticeCopy(props: NoticeProps): { title: string; body: string } {
    const { actorName, versionLabel } = props;

    // Every notice answers the same question second: what is scoring conversations right now.
    const live = props.isThisVersionLive
        ? "This version is published and is scoring conversations now."
        : props.liveVersionLabel
        ? `Nothing is live from it. ${props.liveVersionLabel} keeps scoring conversations until a new version is published.`
        : "Nothing is live from it. This model has no published version.";

    const sentences = (first: string, last: string) =>
        [first, live, last].join(" ");

    switch (props.event) {
        case ApprovalEvent.ApprovalRequested:
            return {
                title: "Approval requested",
                body: sentences(
                    `${versionLabel} is waiting for review.`,
                    "Approving it does not publish it.",
                ),
            };
        case ApprovalEvent.Approved:
            return {
                title: "Version approved",
                body: sentences(
                    `${versionLabel} passed review and is cleared to publish.`,
                    "Approval does not publish a version. Publish it when you are ready.",
                ),
            };
        case ApprovalEvent.ChangesRequested:
            return {
                title: "Changes requested",
                body: sentences(
                    "This version is back in draft.",
                    `Editing and saving sends it back to ${actorName}.`,
                ),
            };
        case ApprovalEvent.Rejected:
            return {
                title: "Version rejected",
                body: sentences(
                    "This approval request is closed.",
                    "A new approval request is required before this version can be reviewed again.",
                ),
            };
        default:
            return { title: "Approval updated", body: live };
    }
}

/**
 * Opened when someone follows an approval email's button. Shows what
 * is live right now, and the action that moves this version forward.
 */
export function ApprovalEmailNoticeDialog(props: NoticeProps) {
    const { title, body } = noticeCopy(props);
    const { Icon, color } = APPROVAL_EVENT_ICONS[props.event];

    return (
        <AcxDialog
            isOpen={props.isOpen}
            onClose={props.onClose}
            maxWidth="sm"
            fullWidth
            hideCancelButton
            title={
                <Stack direction="row" alignItems="center" gap={1}>
                    <Icon sx={{ color, fontSize: 22 }} />
                    {title}
                </Stack>
            }
            dialogContentChildren={
                <Stack gap={2.5}>
                    <Typography variant="body2" color="gray.600">
                        {[props.actorName, props.eventDate]
                            .filter(Boolean)
                            .join(" · ")}
                    </Typography>

                    {props.comment && (
                        <Box
                            sx={(theme) => ({
                                borderLeft: `4px solid ${theme.palette.yellow.main}`,
                                backgroundColor:
                                    theme.palette.yellow.background,
                                px: 2.5,
                                py: 2,
                            })}
                        >
                            <Typography
                                variant="body1"
                                color="gray.800"
                                whiteSpace="pre-wrap"
                            >
                                &ldquo;{props.comment}&rdquo;
                            </Typography>
                        </Box>
                    )}

                    <Typography variant="body1" color="gray.800">
                        {body}
                    </Typography>
                </Stack>
            }
        >
            {/* "Cancel" reads as undoing the decision, so an informational notice closes on "Got it". */}
            {props.action ? (
                <AcxButton onClick={props.action.onClick}>
                    {props.action.label}
                </AcxButton>
            ) : (
                <AcxButton onClick={props.onClose}>Got it</AcxButton>
            )}
        </AcxDialog>
    );
}
